import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mapTo, mergeMap, switchMap } from 'rxjs/operators';
import * as ProjectTypesActions from './actions';
import { environment } from 'src/environments/environment';
import { IField } from '@app/models/field.model';
import { IProjectType } from '@app/models/project-type.model';
import { FieldStoreActions } from '../shared-store/field-store';
import { HttpService } from '@surveyname/common';

@Injectable()
export class ProjectTypeEffects {
  constructor(private http: HttpService, private action$: Actions) { }

  GetProjectTypes$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectTypesActions.GetProjectTypesAction),
      mergeMap(action =>
        this.http.get(`${environment.apiUrl}/project-type`).pipe(
          map((types: IField[]) => {
            return ProjectTypesActions.ProjectTypesLoadedAction({ payload: types });
          }),
          catchError((error: Error) => {
            return of(ProjectTypesActions.ErrorProjectTypesAction(error));
          })
        )
      )
    )
  );

  CreateProjectType$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectTypesActions.BeginCreateProjectTypeAction),
      mergeMap(action =>
        this.http.post(`${environment.apiUrl}/project-type`, action.payload).pipe(
          map((projectType: IProjectType[]) => {
            return ProjectTypesActions.SuccessCreateProjectTypeAction({ payload: projectType });
          }),
          catchError((error: Error) => {
            return of(ProjectTypesActions.ErrorProjectTypesAction(error));
          })
        )
      )
    ),
  );

  UpdateProjectType$: Observable<Action> = createEffect(() =>
  this.action$.pipe(
    ofType(ProjectTypesActions.BeginUpdateProjectTypeAction),
    mergeMap(action =>
    {
       return  this.http.put(`${environment.apiUrl}/project-type`, action.payload).pipe(
        map((projectType: IProjectType[]) => {
          return ProjectTypesActions.SuccessUpdateProjectTypeAction({ payload: projectType });
        }),
        catchError((error: Error) => {
          return of(ProjectTypesActions.ErrorProjectTypesAction(error));
        })
      ); }
    )
  ),
);
RemoveProjectType$: Observable<Action> = createEffect(() =>
this.action$.pipe(
  ofType(ProjectTypesActions.BeginRemoveProjectTypeAction),
  mergeMap(action =>
  {
     return  this.http.delete(`${environment.apiUrl}/project-type/${action.payload._id}`).pipe(
      map(() => {
        return ProjectTypesActions.SuccessRemoveProjectTypeAction({ payload: action.payload });
      }),
      catchError((error: Error) => {
        return of(ProjectTypesActions.ErrorProjectTypesAction(error));
      })
    ); }
  )
),
);

  FetchSelectedProjectType$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectTypesActions.BeginFetchSelectedProjectTypeAction),
      mergeMap(action => {
        return this.http.get(`${environment.apiUrl}/project-type/projectTypeId/${action.payload.typeId}`).pipe(
          map((projectType: IProjectType) => {
            return ProjectTypesActions.SuccessFetchSelectedProjectTypeAction({ payload: projectType });
          }),
          catchError((error: Error) => {
            return of(ProjectTypesActions.ErrorProjectTypesAction(error));
          })
        );
      }
      )
    ),
  );

  AddSection$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectTypesActions.BeginAddSectionAction),
      mergeMap(action => {
        return this.http.patch(`${environment.apiUrl}/project-type/new-section`, action.payload).pipe(
          map((projectType: IProjectType) => {
            return ProjectTypesActions.SuccessFetchSelectedProjectTypeAction({ payload: projectType });
          }),
          catchError((error: Error) => {
            return of(ProjectTypesActions.ErrorProjectTypesAction(error));
          })
        );
      }
      )
    ),
  );

  MoveFieldToSection$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectTypesActions.BeginMoveFieldToSectionAction),
      mergeMap(action => {
        return this.http.patch(`${environment.apiUrl}/project-type/move-field/section`, action.payload).pipe(
          map((projectType: IProjectType) => {
            return ProjectTypesActions.SuccessMoveFieldToSectionAction({ payload: projectType });
          }),
          catchError((error: Error) => {
            return of(ProjectTypesActions.ErrorProjectTypesAction(error));
          })
        );
      }
      )
    ),
  );

  MoveSection$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectTypesActions.BeginMoveSectionAction),
      mergeMap(action => {
        return this.http.patch(`${environment.apiUrl}/project-type/move-section`, action.payload).pipe(
          map((projectType: IProjectType) => {
            return ProjectTypesActions.SuccessMoveSectionAction({ payload: { projectType } });
          }),
          catchError((error: Error) => {
            return of(ProjectTypesActions.ErrorProjectTypesAction(error));
          })
        );
      }

      ),
    ));

  CallConnectFieldsToProjectType$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(FieldStoreActions.AddFieldsAction),
      map(action => action.payload),
      map((payload) => ProjectTypesActions.BeginConnectFieldToProjectTypeAction({ payload }))
    )
  );

  ConnectFieldToProjectType$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectTypesActions.BeginConnectFieldToProjectTypeAction),
      mergeMap(action => {
        const addFieldParams = { projectTypeId: action.payload.projectTypeId, fieldIds: action.payload.fields.map((field) => field._id), entityType: action.payload.entityType };
        return this.http.patch(environment.apiUrl + '/project-type/add-fields', addFieldParams).pipe(
          switchMap((projectType: IProjectType) => [
            ProjectTypesActions.SuccessConnectFieldToProjectTypeAction({ payload: { projectType } }),
          ]),
          catchError((error: Error) => {
            return of(ProjectTypesActions.ErrorProjectTypesAction(error));
          })
        );
      }
      )
    ),
  );

  ChangeSectionName$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectTypesActions.BeginChangeSectionNameAction),
      mergeMap(action => {
        const { projectTypeId, sectionId, name } = action.payload;
        return this.http.patch(environment.apiUrl + '/project-type/rename-section', { projectTypeId, sectionId, name }).pipe(
          map((projectType: IProjectType) => {
            return ProjectTypesActions.SuccessChangeSctionNameAction({ payload: { projectType } });
          }),
          catchError((error: Error) => {
            return of(ProjectTypesActions.ErrorProjectTypesAction(error));
          })
        );
      }
      )
    ),
  );
}

